import { Flex } from "antd";
import { colors } from "shared/theme/color";
import { textPx } from "shared/constants/zoom";

// List-header stat band: a page's TIMELESS facts (counts/composition —
// anything time-windowed or ranked lives in Analitik) as one segmented band
// in the dashboard activity-strip idiom. Every segment is tappable and
// filters the page's list to its slice; the selected segment carries the
// navy tint (it genuinely IS a selected state — don't use this band for
// purely informational stats, where the tint would lie). Interactive states
// (hover/selected/focus) live in index.css under .stat-band-segment.
export type StatBandSegment = {
  key: string;
  value: number;
  label: string;
};

// Page-scale variant of SessionDetailModal's InlineStat, centred over its
// label (the migration modal band's treatment: these labels are far wider
// than a 1-2 digit value, so left-aligned numerals read unevenly spaced).
const InlineStat = ({
  value,
  label,
  tone,
}: {
  value: number;
  label: string;
  tone: "ink" | "navy";
}) => (
  <div>
    <div
      style={{
        fontFamily: "var(--font-display)",
        fontSize: textPx(26),
        fontWeight: 600,
        letterSpacing: "-0.01em",
        color: tone === "navy" ? colors.navyDark : colors.ink,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {value}
    </div>
    <div
      style={{
        fontSize: textPx(13),
        color: tone === "navy" ? colors.navy : colors.ink3,
        marginTop: 2,
      }}
    >
      {label}
    </div>
  </div>
);

const StatBand = ({
  segments,
  selectedKey,
  onSelect,
}: {
  segments: StatBandSegment[];
  selectedKey: string;
  onSelect: (key: string) => void;
}) => (
  <Flex
    style={{
      alignSelf: "flex-start",
      background: colors.card,
      border: `1px solid ${colors.lineStrong}`,
      borderRadius: 12,
      overflow: "hidden",
    }}
  >
    {segments.map((seg, i) => {
      const selected = selectedKey === seg.key;
      return (
        <button
          key={seg.key}
          type="button"
          className={`stat-band-segment${
            selected ? " stat-band-segment-active" : ""
          }`}
          aria-pressed={selected}
          onClick={() => onSelect(seg.key)}
          style={{
            minWidth: 124,
            padding: "14px 20px",
            border: "none",
            borderLeft: i === 0 ? "none" : `1px solid ${colors.lineStrong}`,
            textAlign: "center",
            cursor: "pointer",
          }}
        >
          <InlineStat
            value={seg.value}
            label={seg.label}
            tone={selected ? "navy" : "ink"}
          />
        </button>
      );
    })}
  </Flex>
);

export default StatBand;
